import { useState, useEffect, useRef } from "react";
import { ShieldCheck, Check, ChevronLeft, Plus, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { C } from "./data";

const LINE = "#E3E0D8";
import { getPlans, getHousehold, startSubscription, signSubscription } from "./consumerAuth";

const card = { background: "#fff", border: `1px solid ${LINE}`, borderRadius: 14, padding: 14, marginBottom: 12 };
const label = { fontSize: 11, fontWeight: 700, color: C.slate, textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 5, display: "block" };
const input = { width: "100%", padding: "11px 12px", border: `1px solid ${LINE}`, borderRadius: 10, fontSize: 14, fontFamily: "inherit", boxSizing: "border-box", background: "#fff", color: C.ink };
const cta = (busy) => ({ width: "100%", padding: 14, background: C.red, color: "#fff", border: 0, borderRadius: 12, fontSize: 15, fontWeight: 700, cursor: busy ? "default" : "pointer", opacity: busy ? 0.7 : 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 8 });

function SignaturePad({ onChange }) {
  const ref = useRef(null);
  const drawing = useRef(false);
  const pos = (e) => {
    const r = ref.current.getBoundingClientRect();
    const t = e.touches ? e.touches[0] : e;
    return { x: (t.clientX - r.left) * (ref.current.width / r.width), y: (t.clientY - r.top) * (ref.current.height / r.height) };
  };
  const start = (e) => { e.preventDefault(); drawing.current = true; const ctx = ref.current.getContext("2d"); const p = pos(e); ctx.beginPath(); ctx.moveTo(p.x, p.y); };
  const move = (e) => { if (!drawing.current) return; e.preventDefault(); const ctx = ref.current.getContext("2d"); const p = pos(e); ctx.lineWidth = 2.5; ctx.lineCap = "round"; ctx.strokeStyle = C.ink; ctx.lineTo(p.x, p.y); ctx.stroke(); };
  const end = () => { if (drawing.current) { drawing.current = false; onChange(ref.current.toDataURL("image/png")); } };
  const clear = () => { const c = ref.current; c.getContext("2d").clearRect(0, 0, c.width, c.height); onChange(null); };
  return (
    <div>
      <canvas ref={ref} width={520} height={150} data-testid="sub-signature-pad"
        onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
        onTouchStart={start} onTouchMove={move} onTouchEnd={end}
        style={{ width: "100%", height: 120, background: "#fff", border: `1px dashed ${LINE}`, borderRadius: 10, touchAction: "none" }} />
      <button type="button" onClick={clear} data-testid="sub-signature-clear"
        style={{ marginTop: 6, background: "none", border: 0, color: C.slate, fontSize: 12, textDecoration: "underline", cursor: "pointer" }}>Clear signature</button>
    </div>
  );
}

export default function Subscribe({ nav, user, onDone }) {
  const [step, setStep] = useState("plan");
  const [plans, setPlans] = useState([]);
  const [plan, setPlan] = useState(null);
  const [household, setHousehold] = useState([]);
  const [picked, setPicked] = useState([]);
  const [extra, setExtra] = useState([]);
  const [sub, setSub] = useState(null);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({ name: (user?.name || "").split(" ")[0] || "", surname: (user?.name || "").split(" ").slice(1).join(" "), id_number: "", mobile: user?.cell || "", email: user?.email || "", home_address: "", account_holder: user?.name || "", bank: "", branch_name: "", branch_code: "", account_number: "", account_type: "Cheque/Current", signature: null });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  useEffect(() => {
    getPlans().then((d) => setPlans(d.plans || [])).catch(() => toast.error("Couldn't load the plans"));
    getHousehold().then((d) => setHousehold(d.dependents || [])).catch(() => setHousehold([]));
  }, []);

  const seats = plan ? plan.max_members - 1 : 0;
  const toggle = (id) => setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : (p.length + extra.length < seats ? [...p, id] : (toast.error(`The ${plan.label} plan covers ${plan.max_members} people`), p))));

  const choosePlan = (p) => { setPlan(p); setPicked([]); setExtra([]); setStep(p.max_members > 1 ? "members" : "mandate"); };

  // Single-seat plans skip the member step, so the subscription record is created lazily here.
  const ensureSub = async () => {
    if (sub) return sub;
    const d = await startSubscription({ plan_code: plan.code, member_ids: picked, new_members: extra });
    setSub(d.subscription);
    return d.subscription;
  };

  const beginMandate = async () => {
    setBusy(true);
    try {
      await ensureSub();
      setStep("mandate");
    } catch (e) {
      toast.error("Couldn't start the sign-up", { description: e.response?.data?.detail || "Please try again." });
    } finally { setBusy(false); }
  };

  const submit = async () => {
    for (const [k, name] of [["name", "First name"], ["surname", "Surname"], ["id_number", "ID number"], ["account_holder", "Account holder"], ["bank", "Bank"], ["branch_code", "Branch code"], ["account_number", "Account number"]]) {
      if (!String(f[k] || "").trim()) return toast.error(`${name} is required`);
    }
    if (String(f.id_number).replace(/\D/g, "").length !== 13) return toast.error("Enter your 13-digit South African ID number");
    if (!f.signature) return toast.error("Please sign the debit-order authority");
    setBusy(true);
    try {
      const s = await ensureSub();
      const d = await signSubscription(s.id, f);
      toast.success("You're signed up", { description: "Your panic button switches on in 24 hours." });
      onDone(d.subscription);
    } catch (e) {
      toast.error("Couldn't submit", { description: e.response?.data?.detail || "Please try again." });
    } finally { setBusy(false); }
  };

  const back = () => (step === "plan" ? nav("home") : setStep(step === "mandate" && plan?.max_members > 1 ? "members" : "plan"));

  return (
    <div style={{ padding: "18px 18px 40px" }} data-testid="subscribe-screen">
      <button onClick={back} data-testid="sub-back" style={{ background: "none", border: 0, color: C.slate, fontSize: 13, display: "flex", alignItems: "center", gap: 4, marginBottom: 14, cursor: "pointer", padding: 0 }}>
        <ChevronLeft size={16} /> Back
      </button>

      {step === "plan" && (
        <>
          <h1 style={{ fontSize: 22, fontWeight: 800, color: C.ink, margin: "0 0 6px", letterSpacing: "-0.02em" }}>Activate emergency response</h1>
          <p style={{ fontSize: 13.5, color: C.slate, lineHeight: 1.55, margin: "0 0 18px" }}>
            Family tracking stays free. Add a Security Package to unlock the panic button and have armed response dispatched to your location.
          </p>
          {plans.map((p) => (
            <button key={p.code} onClick={() => choosePlan(p)} data-testid={`plan-${p.code}`}
              style={{ ...card, width: "100%", textAlign: "left", cursor: "pointer", display: "flex", alignItems: "center", gap: 12 }}>
              <div style={{ width: 40, height: 40, borderRadius: 11, background: `${C.red}14`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <ShieldCheck size={20} color={C.red} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 15, fontWeight: 700, color: C.ink }}>{p.label}</div>
                <div style={{ fontSize: 12.5, color: C.slate, marginTop: 2 }}>{p.blurb}</div>
              </div>
              <div style={{ textAlign: "right" }}>
                <div style={{ fontSize: 19, fontWeight: 800, color: C.ink, letterSpacing: "-0.02em" }}>R{p.price}</div>
                <div style={{ fontSize: 10.5, color: C.slate }}>per month</div>
              </div>
            </button>
          ))}
          <p style={{ fontSize: 11.5, color: C.slate, lineHeight: 1.6, marginTop: 4 }}>
            Prices include VAT. Your first full month is debited on the 1st of next month — the rest of this month is free. Cover starts 24 hours after you sign.
          </p>
        </>
      )}

      {step === "members" && (
        <>
          <h1 style={{ fontSize: 20, fontWeight: 800, color: C.ink, margin: "0 0 4px" }}>Who else is covered?</h1>
          <p style={{ fontSize: 13, color: C.slate, margin: "0 0 16px" }}>{plan.label} · R{plan.price}pm · choose up to {seats} {seats === 1 ? "person" : "people"}.</p>
          {household.map((m) => {
            const on = picked.includes(m.id);
            return (
              <button key={m.id} onClick={() => toggle(m.id)} data-testid={`member-${m.id}`}
                style={{ ...card, width: "100%", textAlign: "left", cursor: "pointer", display: "flex", alignItems: "center", gap: 11, borderColor: on ? C.red : LINE }}>
                <div style={{ width: 22, height: 22, borderRadius: 6, border: `1.5px solid ${on ? C.red : LINE}`, background: on ? C.red : "#fff", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  {on && <Check size={14} color="#fff" />}
                </div>
                <div>
                  <div style={{ fontSize: 14.5, fontWeight: 600, color: C.ink }}>{m.full_name}</div>
                  <div style={{ fontSize: 12, color: C.slate }}>{m.role}</div>
                </div>
              </button>
            );
          })}
          {extra.map((m, i) => (
            <div key={`x${i}`} style={{ ...card, display: "flex", alignItems: "center", gap: 10 }}>
              <Check size={16} color={C.green} />
              <div style={{ flex: 1, fontSize: 14, color: C.ink }}>{m.name} <span style={{ color: C.slate, fontSize: 12 }}>· {m.relationship}</span></div>
              <button onClick={() => setExtra(extra.filter((_, j) => j !== i))} style={{ background: "none", border: 0, color: C.slate, fontSize: 12, cursor: "pointer", textDecoration: "underline" }}>remove</button>
            </div>
          ))}
          {picked.length + extra.length < seats && (
            <AddPerson onAdd={(m) => setExtra([...extra, m])} />
          )}
          <button onClick={beginMandate} disabled={busy} data-testid="sub-continue" style={{ ...cta(busy), marginTop: 14 }}>
            {busy ? <><Loader2 size={16} style={{ animation: "rqspin 0.9s linear infinite" }} /> One moment…</> : "Continue to debit order"}
          </button>
          <style>{"@keyframes rqspin{to{transform:rotate(360deg)}}"}</style>
        </>
      )}

      {step === "mandate" && (
        <>
          <h1 style={{ fontSize: 20, fontWeight: 800, color: C.ink, margin: "0 0 4px" }}>Debit order authority</h1>
          <p style={{ fontSize: 13, color: C.slate, margin: "0 0 16px" }}>{plan.label} · <b style={{ color: C.ink }}>R{plan.price} per month</b>, VAT included. Processed by Acuideas (Pty) Ltd on the 1st of each month.</p>
          <div style={card}>
            <Row><Field l="First name" v={f.name} on={(v) => set("name", v)} t="sub-name" /><Field l="Surname" v={f.surname} on={(v) => set("surname", v)} t="sub-surname" /></Row>
            <Row><Field l="ID number" v={f.id_number} on={(v) => set("id_number", v)} t="sub-id" ph="13 digits" /><Field l="Cell number" v={f.mobile} on={(v) => set("mobile", v)} t="sub-cell" /></Row>
            <Field l="Home address" v={f.home_address} on={(v) => set("home_address", v)} t="sub-address" />
          </div>
          <div style={card}>
            <Field l="Account holder" v={f.account_holder} on={(v) => set("account_holder", v)} t="sub-holder" />
            <Row><Field l="Bank" v={f.bank} on={(v) => set("bank", v)} t="sub-bank" /><Field l="Branch code" v={f.branch_code} on={(v) => set("branch_code", v)} t="sub-branch-code" /></Row>
            <Row>
              <Field l="Account number" v={f.account_number} on={(v) => set("account_number", v)} t="sub-account" />
              <div style={{ flex: 1, minWidth: 0 }}>
                <span style={label}>Account type</span>
                <select value={f.account_type} onChange={(e) => set("account_type", e.target.value)} data-testid="sub-account-type" style={input}>
                  <option>Cheque/Current</option><option>Savings</option><option>Transmission</option>
                </select>
              </div>
            </Row>
          </div>
          <div style={card}>
            <span style={label}>Signature</span>
            <SignaturePad onChange={(v) => set("signature", v)} />
            <p style={{ fontSize: 11.5, color: C.slate, lineHeight: 1.6, marginTop: 8 }}>
              By signing you authorise Acuideas (Pty) Ltd to debit the account above with the agreed ResQ1 subscription fees. Your cover and panic button activate 24 hours after signing. To cancel, contact your broker or ResQ1.
            </p>
          </div>
          <button onClick={submit} disabled={busy} data-testid="sub-submit" style={cta(busy)}>
            {busy ? <><Loader2 size={16} style={{ animation: "rqspin 0.9s linear infinite" }} /> Submitting…</> : `Sign and activate · R${plan.price}pm`}
          </button>
          <style>{"@keyframes rqspin{to{transform:rotate(360deg)}}"}</style>
        </>
      )}
    </div>
  );
}

const Row = ({ children }) => <div style={{ display: "flex", gap: 10 }}>{children}</div>;

const Field = ({ l, v, on, t, ph }) => (
  <div style={{ flex: 1, marginBottom: 11 }}>
    <span style={label}>{l}</span>
    <input value={v || ""} placeholder={ph || ""} onChange={(e) => on(e.target.value)} data-testid={t} style={input} />
  </div>
);

function AddPerson({ onAdd }) {
  const [open, setOpen] = useState(false);
  const [m, setM] = useState({ name: "", cell: "", relationship: "Dependant" });
  if (!open) {
    return (
      <button onClick={() => setOpen(true)} data-testid="sub-add-person"
        style={{ ...card, width: "100%", textAlign: "left", cursor: "pointer", display: "flex", alignItems: "center", gap: 10, borderStyle: "dashed", color: C.slate, fontSize: 14 }}>
        <Plus size={16} /> Add someone who isn't listed
      </button>
    );
  }
  return (
    <div style={card}>
      <Row><Field l="Full name" v={m.name} on={(v) => setM({ ...m, name: v })} t="new-member-name" /><Field l="Cell number" v={m.cell} on={(v) => setM({ ...m, cell: v })} t="new-member-cell" /></Row>
      <Field l="Relationship" v={m.relationship} on={(v) => setM({ ...m, relationship: v })} t="new-member-rel" />
      <div style={{ display: "flex", gap: 8 }}>
        <button onClick={() => { if (!m.name.trim()) return toast.error("Enter their name"); onAdd(m); setM({ name: "", cell: "", relationship: "Dependant" }); setOpen(false); }}
          data-testid="new-member-save" style={{ flex: 1, padding: 10, background: C.navy, color: "#fff", border: 0, borderRadius: 10, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>Add</button>
        <button onClick={() => setOpen(false)} style={{ padding: "10px 14px", background: "#fff", color: C.slate, border: `1px solid ${LINE}`, borderRadius: 10, fontSize: 13, cursor: "pointer" }}>Cancel</button>
      </div>
    </div>
  );
}
